import { Button } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

type UnsavedChangesModalProps = {
  shouldBlock: boolean
  confirmDiscard: () => void
}
export const UnsavedChangesModal = ({
  shouldBlock,
  confirmDiscard,
}: UnsavedChangesModalProps) => {
  const history = useHistory()
  const currentLocation = useLocation()
  const nextLocation = useRef<{
    location: typeof history.location
    action: 'PUSH' | 'REPLACE' | 'POP'
  } | null>(null)
  const unblockRef = useRef<ReturnType<typeof history.block> | null>(null)
  const currentPathRef = useRef(currentLocation.pathname)
  const [showModal, setShowModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const beforeUnload = (event: BeforeUnloadEvent) => {
    event.preventDefault()
    event.returnValue = ''
  }

  const isInsideSettings = (path: string) =>
    /^\/forms\/[^/]+\/settings$/.test(path)
  const isInsideDesign = (path: string) => /^\/forms\/[^/]+\/design$/.test(path)
  const isFormRoot = (path: string) => /^\/forms\/[^/]+$/.test(path)
  const isFormNew = (path: string) => path.includes('/forms/new')

  const isAllowedInternalNavigation = (from: string, to: string) => {
    if (isFormNew(to) || isFormNew(from)) return false
    const validFrom =
      isInsideSettings(from) || isInsideDesign(from) || isFormRoot(from)
    const validTo = isInsideSettings(to) || isInsideDesign(to) || isFormRoot(to)

    return validFrom || validTo
  }

  const handleDiscard = () => {
    try {
      setIsLoading(true)
      confirmDiscard()

      setShowModal(false)
      unblockRef.current?.()

      const nav = nextLocation.current
      if (nav?.action === 'PUSH') {
        history.push(nav.location.pathname)
      }
      if (nav?.action === 'REPLACE') {
        history.replace(nav.location.pathname)
      }
      return
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!shouldBlock) return

    unblockRef.current?.()
    unblockRef.current = history.block((location, action) => {
      const currentPath = currentPathRef?.current
      const nextPath = location.pathname

      if (!isAllowedInternalNavigation(currentPath, nextPath)) {
        return
      }

      nextLocation.current = { location, action }
      setShowModal(true)
      return false
    })

    return () => {
      unblockRef.current?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation.pathname, shouldBlock, history])

  useEffect(() => {
    if (!shouldBlock) return
    window.addEventListener('beforeunload', beforeUnload)
    return () => {
      window.removeEventListener('beforeunload', beforeUnload)
    }
  }, [shouldBlock])

  useEffect(() => {
    currentPathRef.current = currentLocation.pathname
  }, [currentLocation.pathname])

  if (!showModal) return null
  return (
    <div className="fixed left-0 top-0 z-9999 flex h-screen w-screen justify-center bg-black/15 pt-1/12 backdrop-blur-sm">
      <div className="flex h-fit w-full max-w-md flex-col items-center justify-center gap-1 rounded-lg bg-white p-4 shadow">
        <h1 className="mt-2 w-full text-left text-2xl font-bold text-gray-800">
          Unsaved Changes
        </h1>
        <span className="mt-3 w-full text-left text-gray-500">
          Your unsaved changes will be lost. <br />
          Save changes before closing?
        </span>
        <div className="mt-3 flex w-full justify-between gap-2">
          <Button
            danger
            disabled={isLoading}
            onClick={handleDiscard}
            type="primary"
          >
            Don't Save
          </Button>
          <Button disabled={isLoading} onClick={() => setShowModal(false)}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}
